import UIComponent from "sap/ui/core/UIComponent";
import models from "vamos/cockpit/model/models";
import Device from "sap/ui/Device";
import Config from "vamos/cockpit/config/Config";
import { Client, ResponseListClient } from "vamos/cockpit/model/api/vamos.private.api.types";
import User from "vamos/cockpit/model/User";
import Engine from "sap/m/p13n/Engine";
import P13nService from "vamos/cockpit/base/services/P13nService";
import Messaging from "sap/ui/core/Messaging";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import JSONModel from "sap/ui/model/json/JSONModel";
import ODataMetaModel from "sap/ui/model/odata/v4/ODataMetaModel";
import ErrorService from "vamos/cockpit/base/services/ErrorService";
import TypeErrorService from "vamos/cockpit/base/services/type/ErrorService";
import LoginService from "vamos/cockpit/login/LoginService";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import ClientSettingsService from "vamos/cockpit/base/services/ClientSettingsService";
import Localization from "sap/base/i18n/Localization";
import TabContainer from "sap/m/TabContainer";
import View from "sap/ui/core/mvc/View";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import StringUtil from "vamos/cockpit/base/util/StringUtil";
import UserHandler from "vamos/cockpit/data/user/handler/UserHandler";
import CoreComponent from "sap/ui/core/Component";
import Control from "sap/ui/core/Control";
import { ResponseLoginResponse } from "vamos/cockpit/model/api/vamos.public.api.types";
import SplashScreen from "vamos/cockpit/base/util/SplashScreen";

/**
 * @namespace vamos.cockpit
 */
export default class Component extends UIComponent {
    private contentDensityClass: string;
    private tabContainer: TabContainer;
    private config: Config;
    private loginService: LoginService;
    public loadedViews: Record<string, View> = {};

    private fragments : Map<string, Control | Control[]> = new Map();

    private user: User;

    private appModel: JSONModel;

    private hooks: any = {};

    private isInServerUnreachableMode: boolean;

    private static readonly BACKEND_AVAILABILITY_POLL_INTERVAL_MS = 5000;

    public init(): void {
        super.init();
        this.isInServerUnreachableMode = false;

        // resolve env-config.json overrides once, before any Config instance is created
        void Config.loadEnvConfig().finally(() => {
            this.config = new Config();
            this.loginService = new LoginService();

            TypeErrorService.getInstance().init(this.getModel("i18n") as JSONModel);
            ApiService.getInstance().init(this);

            this.initModels();
            void this.initUserDataOnceBackendAvailable();
        });
    }

    public async isBackendAvailable(): Promise<boolean> {
        try {
            const response = await fetch(this.config.getRestUrl("system/info", true));
            return response.ok;
        } catch {
            return false;
        }
    }

    private async initUserDataOnceBackendAvailable(): Promise<void> {
        if (await this.isBackendAvailable()) {
            await this.initUserData();
            return;
        }

        this.enterServerUnreachableMode();
    }

    /**
     * Shows the "server unreachable" page and polls until the backend is reachable again, then reloads
     * the app. Safe to call repeatedly - a second call while already in this mode is a no-op so only one
     * polling interval ever runs. This is independent of MaintenanceService's import-lock screen.
     */
    public enterServerUnreachableMode(): void {
        if (this.isInServerUnreachableMode) {
            return;
        }
        this.isInServerUnreachableMode = true;
        this.appModel.setProperty("/serverUnreachable", true);
        // the splash screen is otherwise only removed at the end of the happy-path initUserData() flow
        this.removeSplashScreen();

        const intervalId = window.setInterval(() => {
            void this.isBackendAvailable().then((isAvailable) => {
                if (isAvailable) {
                    window.clearInterval(intervalId);
                    window.location.reload();
                }
            });
        }, Component.BACKEND_AVAILABILITY_POLL_INTERVAL_MS);
    }

    private async initUserData() {
        const oMetaModel = this.getModel().getMetaModel() as ODataMetaModel;

        const data = await oMetaModel.requestData();
        this.loadEnums(data);
        await this.refreshClientList();
        await this.refreshUser();

        Engine.getInstance().attachStateChange(P13nService.handleStateChange);

        const oMessageBinding = Messaging.getMessageModel().bindList(
            "/",
            undefined,
            [],
            new Filter("technical", FilterOperator.EQ, true),
        );
        oMessageBinding.attachChange(ErrorService.onMessageBindingChange, this);
        // create the device model
        this.startSessionPingService();
        // create the views based on the url/hash
        this.getRouter().initialize();

        this.removeSplashScreen();
        ClientSettingsService.getInstance().init(this);
    }

    private initModels() {
        this.appModel = this.getModel("app") as JSONModel;
        this.appModel.setProperty("/StepPercentage", 0);
        this.appModel.setProperty("/serverUnreachable", false);
        this.setModel(models.createDeviceModel(), "device");
        this.setModel(new JSONModel({}), "ValueHelp");

        this.appModel.setProperty("/ClientSettings", {});
        this.appModel.setProperty("/hasMessages", false);
        this.setModel(
            new JSONModel(sap.ui.require.toUrl("vamos/cockpit/config/UiSettings.json")),
            "defaultUiSettings",
        );
    }

    public addHook(name: string, func: (...params: any[]) => void): void {
        if (!this.hooks[name]) this.hooks[name] = [];
        this.hooks[name].push(func);
    }

    public getHook(name: string) {
        if (this.hooks[name]) return this.hooks[name];
        else return null;
    }

    runHook(name: string, ...params: any[]) {
        if (this.hooks[name]) {
            this.hooks[name].forEach((func: (...params: any[]) => void) => func(...params));
        }
    }

    public getConfig(): Config {
        return this.config;
    }

    public getContentDensityClass(): string {
        if (this.contentDensityClass === undefined) {
            // check whether FLP has already set the content density class; do nothing in this case
            if (document.body.classList.contains("sapUiSizeCozy") || document.body.classList.contains("sapUiSizeCompact")) {
                this.contentDensityClass = "";
            } else if (!Device.support.touch) {
                // apply "compact" mode if touch is not supported
                this.contentDensityClass = "sapUiSizeCompact";
            } else {
                // "cozy" in case of touch support; default for most sap.m controls, but needed for desktop-first controls like sap.ui.table.Table
                this.contentDensityClass = "sapUiSizeCozy";
            }
        }
        return this.contentDensityClass;
    }

    public removeSplashScreen(): void {
        SplashScreen.remove();
    }

    private startSessionPingService() {
        setInterval(() => {
            ApiService.getInstance()
                .getRestJsonData("/user/login", true)
                .then(
                    (result: ResponseLoginResponse) => {
                        if (!result.content.userLoggedIn) {
                            this.loginService.redirectToLogin();
                        }
                    },
                    () => {
                        console.error("pingServer", "failed to ping the server to extend the session");
                    },
                );
        }, 600000); // 2 min;
    }

    public async refreshClientList(): Promise<void> {
        const oAppModel = this.getModel("app") as JSONModel;

        const clientListResponse: ResponseListClient =
            await ApiService.getInstance().getRestJsonData("/user/clients");
        let currentClientName = "";
        const clientList = clientListResponse.content;
        clientList.forEach((client: Client) => {
            if (client.selected) {
                currentClientName = client.name;
            }
        });

        let clients = {
            CurrentClientName: currentClientName,
            ClientList: clientList,
            HasMoreClients: clientList.length > 1,
        };
        oAppModel.setProperty("/Clients", clients);
    }

    public async refreshUser() {
        const oAppModel = this.getModel("app") as JSONModel;

        const oUser = await UserHandler.getInstance().getUser();
        oAppModel.setProperty("/User", oUser);
        this.setUser(oUser);
        this.setLanguage(oUser.Language);
        this.setOnlyShowShoppingCard(oUser)
    }

    public getUser(): User {
        return this.user;
    }

    public setUser(user: User) {
        this.user = user;
    }

    public setLanguage(language: string) {
        Localization.setLanguage(language);
    }

    public getTabContainer(): TabContainer {
        return this.tabContainer;
    }

    public setTabContainer(tabContainer: TabContainer) {
        this.tabContainer = tabContainer;
    }

    public redirectToLogin() {
        this.loginService.redirectToLogin();
    }

    public getLoadedView(viewName: string): View {
        return this.loadedViews[viewName];
    }

    public setLoadedView(name: string, view: View): void {
        this.loadedViews[name] = view;
    }

    public loadEnums(metaData: any): void {
        let enumModel: JSONModel = this.getModel("enum") as JSONModel;
        if (!enumModel) {
            enumModel = new JSONModel({});
        }
        const enums = [];
        for (const propertyName in metaData) {
            const key = propertyName;
            const entity: any = metaData[key];
            if (metaData[key].$kind !== "EnumType") {
                continue;
            }
            const enumName = propertyName.split(".")[1];
            for (const enumMember in entity) {
                if (enumMember.startsWith("$")) {
                    continue;
                }
                const resourceModel: ResourceModel = this.getModel("i18n") as ResourceModel;
                const bundle: ResourceBundle = resourceModel.getResourceBundle() as ResourceBundle;

                const key = StringUtil.stringToUpperSnakeCase(enumName) + "_" + enumMember;
                if(bundle.hasText(key)){
                    const text = bundle.getText(key);

                    enums.push({ key: enumName, intValue: entity[enumMember], value: enumMember, translatedText: text });
                }

            }
        }
        enumModel.setProperty("/items", enums);
        this.setModel(enumModel, "enum");
    }

    public static getComponent(): Component {
        return CoreComponent.getComponentById("vamos_component") as Component;
    }

    public getFragment(name: string): Control | Control[] {
        return this.fragments.get(name);
    }

    public setFragment(name: string, fragment: Control | Control[]) {
        this.fragments.set(name, fragment);
    }
    public setOnlyShowShoppingCard(user: User) {
        const onlyShowShoppingCard = user.IsOnlyInstaller();
        const model = this.getModel("app") as JSONModel;
        model.setProperty("/onlyShowShoppingCard", onlyShowShoppingCard);
    }

}
